// LVGL color formats supported by the image converter.
// Ported from the `ColorFormat` enum in LVGLImage.py. The C output references the
// format by name (LV_COLOR_FORMAT_<NAME>), so only bpp / palette size are needed here.

// Lowercase format names — single source of truth consumed by resource_converter.ts and LVGLSchema.ts.
export const COLOR_FORMAT_NAMES = [
    'l8',
    'i1',
    'i2',
    'i4',
    'i8',
    'a1',
    'a2',
    'a4',
    'a8',
    'argb8888',
    'xrgb8888',
    'rgb565',
    'rgb565a8',
    'argb8565',
    'rgb888'
] as const;
export const ALL_COLOR_FORMAT_NAMES = [...COLOR_FORMAT_NAMES, 'auto', 'raw', 'raw_alpha'] as const;

export type ColorFormat =
    | 'L8'
    | 'I1'
    | 'I2'
    | 'I4'
    | 'I8'
    | 'A1'
    | 'A2'
    | 'A4'
    | 'A8'
    | 'ARGB8888'
    | 'XRGB8888'
    | 'RGB565'
    | 'RGB565A8'
    | 'ARGB8565'
    | 'RGB888';

export type RawColorFormat = 'RAW' | 'RAW_ALPHA';

const BPP: Record<ColorFormat, number> = {
    L8: 8,
    I1: 1,
    I2: 2,
    I4: 4,
    I8: 8,
    A1: 1,
    A2: 2,
    A4: 4,
    A8: 8,
    ARGB8888: 32,
    XRGB8888: 32,
    RGB565: 16,
    RGB565A8: 16, // 16bpp + a8 map
    ARGB8565: 24,
    RGB888: 24
};

const NCOLORS: Partial<Record<ColorFormat, number>> = { I1: 2, I2: 4, I4: 16, I8: 256 };

export const bpp = (cf: ColorFormat): number => BPP[cf];

// Number of palette colors for indexed formats, 0 otherwise.
export const ncolors = (cf: ColorFormat): number => NCOLORS[cf] ?? 0;

export const isIndexed = (cf: ColorFormat): boolean => ncolors(cf) > 0;

export const isAlphaOnly = (cf: ColorFormat): boolean => cf === 'A1' || cf === 'A2' || cf === 'A4' || cf === 'A8';

// Default stride (1-byte aligned), i.e. bytes per pixel row of the bitmap.
export const strideDefault = (cf: ColorFormat, w: number): number => Math.floor((w * bpp(cf) + 7) / 8);

// Total bitmap byte length excluding the image header (palette + rows + optional A8 map).
export const dataLen = (cf: ColorFormat, w: number, h: number): number => {
    const palette = isIndexed(cf) && w * h ? ncolors(cf) * 4 : 0;
    const stride = strideDefault(cf, w);
    const alpha = cf === 'RGB565A8' ? Math.floor(stride / 2) * h : 0;
    return palette + stride * h + alpha;
};

// Map the editor's lowercase color_format value to a ColorFormat. 'auto' returns null:
// the caller quantizes and then picks an indexed format from the resulting palette size.
export const parseColorFormat = (value: string | undefined): ColorFormat | null => {
    const v = (value ?? 'argb8888').toLowerCase();
    if (v === 'auto') return null;
    const cf = v.toUpperCase();
    if (cf in BPP) return cf as ColorFormat;
    throw new Error(`Unsupported image color_format '${value}'`);
};
